import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { HEROES, HERO_ORDER } from "../data/heroes";
import { ACTIVITY_CATALOG, TOTAL_ACTIVITIES } from "../data/activityCatalog";
import HeroNarrateButton from "./HeroNarrateButton";

const ActivityCatalogView = ({ onStart, onStartHeroLed }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const [openActivity, setOpenActivity] = useState(null);

  return (
    <div className="space-y-6" data-testid="activity-catalog">
      <div className="text-center mb-4">
        <h2 className="text-2xl font-extrabold text-gray-900">
          {TOTAL_ACTIVITIES}+ {t("activities.pageTitle")}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {lang === "es"
            ? "Actividades organizadas por cada Súper Héroe del Cerebro."
            : "Activities organized under each Brain Superhero."}
        </p>
      </div>

      <Tabs defaultValue="queen" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-4">
          {HERO_ORDER.map((hk) => {
            const hero = HEROES[hk];
            const tr = t(`heroes.${hk}`, { returnObjects: true });
            return (
              <TabsTrigger
                key={hk}
                value={hk}
                data-testid={`catalog-tab-${hk}`}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:text-white"
                style={{ "--tw-gradient-stops": hero.gradient }}
              >
                <span>{tr.pathTitle} ({ACTIVITY_CATALOG[hk].length})</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {HERO_ORDER.map((hk) => {
          const hero = HEROES[hk];
          const tr = t(`heroes.${hk}`, { returnObjects: true });
          return (
            <TabsContent key={hk} value={hk}>
              <div className={`mb-4 rounded-xl bg-gradient-to-r ${hero.soft} p-4 flex items-center gap-3`}>
                <div className={`${hero.imageBg} rounded-lg p-1 w-16 h-16 flex items-end justify-center flex-shrink-0`}>
                  <img src={hero.image} alt={tr.name} className="max-w-full max-h-full object-contain" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">{tr.name}</p>
                  <p className="text-xs text-gray-700 italic">"{tr.tagline}"</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {ACTIVITY_CATALOG[hk].map((a) => (
                  <Card
                    key={a.id}
                    data-testid={`catalog-activity-${a.id}`}
                    className={`p-4 cursor-pointer hover:-translate-y-1 transition-transform border-l-4`}
                    style={{ borderLeftColor: hero.accent }}
                    onClick={() => setOpenActivity({ ...a, hero: hk })}
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-3xl flex-shrink-0">{a.icon}</div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-gray-900 text-sm leading-tight">{a.title[lang]}</h4>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">{a.desc[lang]}</p>
                        <Badge variant="outline" className="mt-2 text-xs">{a.duration} min</Badge>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Activity Detail Dialog */}
      <Dialog open={!!openActivity} onOpenChange={(v) => !v && setOpenActivity(null)}>
        <DialogContent className="max-w-lg" data-testid="catalog-activity-dialog">
          {openActivity && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-4xl">{openActivity.icon}</div>
                  <div>
                    <DialogTitle className="text-xl">{openActivity.title[lang]}</DialogTitle>
                    <DialogDescription>
                      {t(`heroes.${openActivity.hero}.pathTitle`)} · {openActivity.duration} min
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <p className="text-sm text-gray-700">{openActivity.desc[lang]}</p>
              <ol className="space-y-2 text-sm text-gray-800 list-decimal list-inside">
                {openActivity.instructions[lang].map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
              <div className="flex flex-wrap gap-2 pt-2">
                <HeroNarrateButton
                  hero={openActivity.hero}
                  text={openActivity.instructions[lang].join(". ")}
                  label={lang === "es" ? "Hazlo conmigo" : "Do this with me"}
                  guided={true}
                />
                <Button
                  onClick={() => {
                    if (onStartHeroLed) {
                      onStartHeroLed(openActivity.hero, openActivity);
                    } else if (onStart) {
                      onStart(openActivity.base);
                    }
                    setOpenActivity(null);
                  }}
                  className={`flex-1 bg-gradient-to-r ${HEROES[openActivity.hero].gradient} text-white border-0`}
                  data-testid={`catalog-start-${openActivity.id}`}
                >
                  {t("activities.startActivity")} →
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivityCatalogView;
